/**
 * @file src/pages/company-onboarding-wizard/index.jsx
 * @description Company onboarding wizard — thin orchestrator.
 *
 * What changed from the previous version:
 *  - hasSubmittedRef guards the existing-company redirect from firing
 *    after a successful submit. Without this guard, after submit:
 *      1. useOnboardingActions navigates to COMPANY_SUCCESS
 *      2. queryClient.invalidateQueries refetches company
 *      3. existingCompany becomes truthy
 *      4. The useEffect fires navigate(DASHBOARD) — competing with #1
 *      5. Both navigations race causing the loop
 *
 *    The ref tracks whether we just submitted; if so, skip the redirect
 *    because useOnboardingActions is already handling navigation.
 *
 *  - submitRef.current = handleSubmit moved into useEffect so it does
 *    not run during render (which is technically a side effect)
 */

import React, { useEffect, useCallback, useRef } from "react";
import { useNavigate }                            from "react-router-dom";
import { useGetCompany }                          from "pages/company-settings/useGetCompany";
import LoadingOverlay                             from "components/ui/LoadingOverlay";
import WizardShell                                from "./components/WizardShell";
import StepRouter                                 from "./components/StepRouter";
import useOnboardingForm                          from "./hooks/useOnboardingForm";
import useOnboardingActions                       from "./hooks/useOnboardingActions";
import { ROUTES }                                 from "config/routes";

// ---------------------------------------------------------------------------
// CompanyOnboardingWizard
// ---------------------------------------------------------------------------

const CompanyOnboardingWizard = () => {
  const navigate = useNavigate();

  // ── Existing company check ────────────────────────────────────────────────
  const { data: existingCompany, isLoading: isCheckingCompany } = useGetCompany();

  // Tracks whether the user has just submitted the form.
  // Prevents the redirect effect from competing with the post-submit
  // navigation in useOnboardingActions.
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (hasSubmittedRef.current) return;
    if (!isCheckingCompany && existingCompany) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [existingCompany, isCheckingCompany, navigate]);

  // ── Ref to break circular dependency between form and actions hooks ──────
  const submitRef = useRef(() => {});

  // ── Form state ────────────────────────────────────────────────────────────
  const {
    formData,
    errors,
    currentStep,
    activeTab,
    updateFormData,
    handleNext,
    handlePrevious,
    handleTabChange,
    setSubmitError,
    clearSavedProgress,
  } = useOnboardingForm({
    onFinalStep: useCallback(() => {
      hasSubmittedRef.current = true;
      submitRef.current();
    }, []),
  });

  // ── Actions ───────────────────────────────────────────────────────────────
  const {
    handleSubmit,
    handleJoin,
    handleSkip,
    isLoading,
    isJoining,
    loadingMessage,
  } = useOnboardingActions({
    formData,
    setSubmitError,
    clearSavedProgress,
  });

  // ── Sync handleSubmit into ref on each render ────────────────────────────
  // useEffect ensures this happens after render, not during it
  useEffect(() => {
    submitRef.current = handleSubmit;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <WizardShell
        activeTab={activeTab}
        currentStep={currentStep}
        onTabChange={handleTabChange}
        submitError={errors.submit}
      >
        <StepRouter
          activeTab={activeTab}
          currentStep={currentStep}
          formData={formData}
          errors={errors}
          updateFormData={updateFormData}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onJoin={handleJoin}
          onSkip={handleSkip}
          isJoining={isJoining}
        />
      </WizardShell>

      <LoadingOverlay
        isLoading={isLoading}
        message={loadingMessage}
        fullScreen
      />
    </>
  );
};

export default CompanyOnboardingWizard;